import { Component, OnInit, Input, HostListener, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { IParameter } from 'app/_models/IParameter';
import { IGridActionRow } from '../_models/IGridActionRow';
import { AppComponent } from 'app/app.component';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { CommunicationService } from 'app/_services/communication.service';
import { IAction } from 'app/_models/IAction';
import { RADController } from 'app/_controllers/radController';
import { IHeader } from 'app/_models/IHeader';
import * as XLSX from 'xlsx';
import { ISettings } from 'app/_models/ISettings';
import { parse } from 'node-html-parser';
import { OverlayPanel } from 'primeng/overlaypanel';
import {
  IconDefinition, faPen, faPlus, faPaste, faCircleNotch, faTrash,
  faCircleInfo, faArrowUpFromBracket, faCheck, faFloppyDisk, faXmark, faChevronDown, faListCheck
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'grid-action-component',
  templateUrl: './grid-action.component.html',
  styleUrls: ['./grid-action.component.css']
})
export class GridActionComponent implements OnInit {
  //#region Fields & Icons
  @Input() settings: ISettings;
  @Input() headers: IHeader[] = [];
  @Input() selectedLines: ISelectedLine[] = [];
  @Input() nodeId: string;
  @Input() userId: string;
  @Input() username: string;
  @Input() action: IAction;
  parameters: IParameter[] = [];
  gridRows: IGridActionRow[] = [];
  isSavingGrid: boolean = false;
  j: number = 0;
  invalidValues: boolean = false;
  pastedDataFromExcel: boolean = false;
  allRowsUpdated: boolean;
  faPen: IconDefinition = faPen;
  faPlus: IconDefinition = faPlus;
  faPaste: IconDefinition = faPaste;
  faCircleNotch: IconDefinition = faCircleNotch;
  faTrash: IconDefinition = faTrash;
  faCircleInfo: IconDefinition = faCircleInfo;
  faArrowUpFromBracket: IconDefinition = faArrowUpFromBracket;
  faCheck: IconDefinition = faCheck;
  faFloppyDisk: IconDefinition = faFloppyDisk;
  faXmark: IconDefinition = faXmark;
  faChevronDown: IconDefinition = faChevronDown;
  faListCheck: IconDefinition = faListCheck;
  //#endregion

  constructor(
    public app: AppComponent,
    private communicationService: CommunicationService,
    private radController: RADController
  ) { }

  ngOnInit(): void {
    this.parameters = this.action.parameters;
    this.loadSourceViewHeaders();
  }

  async loadSourceViewHeaders(): Promise<void> {
    this.parameters = await this.radController.loadSourceViewHeaders(this.parameters, this.nodeId, this.userId);
    this.initialise();
  }

  initialise(): void {
    //set parameter types and populate simple comboboxes, not combolookups
    let childParams = this.parameters.filter(param => param.filterParameter != null);
    this.parameters.forEach(param => {
      param.dateValues = { userDate: '', serverDate: '' };
      param.isParentLookup = childParams.some(childParam => param.caption === childParam.filterParameter);
      param.preselectedValue = '';
      param.opened = false;
      param.multiValueArray = [];
      param.selectedComboId = '-1';

      if (param.type == 'combolookup' && param.data.includes('@USERID'))
        param.data = param.data.replace(/@USERID/g, this.userId);

      if (param.type == 'combo' || param.type == 'combobox')
        param.lookupValues = param.data.split(',');

      if (param.type.includes('check')) param.preselectedValue = null;

      let headersToCheck = param.sourceViewHeaders.length > 0 ? param.sourceViewHeaders : this.headers;
      let header = headersToCheck.find(header => header.caption == param.caption);
      if (header) param.dataType = header.dataType;
      else param.dataType = '';
    });

    this.preselectParameters();
  }

  preselectParameters(): void {
    for (var i = 0; i < this.selectedLines.length; i++) {
      for (let key in this.selectedLines[i].lineData) {
        let parameter = this.parameters.find(parameter => parameter.caption == key || parameter.sourceColumn == key);
        if (parameter) {
          parameter.preselectedValue = '';
          parameter.value = '';
          parameter.dateValues = { userDate: '', serverDate: '' };

          let lineValue = this.selectedLines[i].lineData[key];
          if (lineValue != null) {
            if (parameter.dataType == 'DateTime' && parameter.type.includes('date')) {
              parameter.dateValues.userDate = lineValue.toString();

              let dateValues = lineValue.split('/');
              if (this.settings.dateFormatSetting == 'dd/MM/yyyy')
                parameter.dateValues.serverDate = `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`;

              if (this.settings.dateFormatSetting == 'yyyy/MM/dd')
                parameter.dateValues.serverDate = `${dateValues[1]}/${dateValues[2]}/${dateValues[0]}`;
            } else if (parameter.type.includes('check')) {
              if (lineValue == undefined || lineValue.toString() == '' || lineValue == null) {
                parameter.preselectedValue = null;
                parameter.value = null;
              } else {
                parameter.preselectedValue = lineValue.toString();
                parameter.value = lineValue.toString();
              }
            } else if (parameter.type == 'multiselect' && lineValue.toString() != '') {
              parameter.value = lineValue.toString() + ',';
              parameter.multiValueArray = lineValue.toString().split(',');
            } else {
              let parsedValue = !lineValue.toString().includes('</div>') ? lineValue.toString() : parse(lineValue.toString()).querySelector('div')?.text || '';
              parameter.preselectedValue = parsedValue;
              parameter.value = parsedValue;              
            }

            if (parameter.isParentLookup) {
              let childParams = this.parameters.filter(param => param.filterParameter != null && param.filterParameter == parameter.caption);
              childParams.forEach(childParam => { childParam.filterValue = parameter.preselectedValue; })             
            }
          }
        }
      }

      this.gridRows.push({
        id: this.gridRows.length + 1,
        params: JSON.parse(JSON.stringify(this.parameters)),
        saveResult: '',
        message: '',
        sqlError: '',
        datesFormatted: false
      });
    }

    if (this.gridRows.length == 0) return;
    this.j = 0;
    this.app.showLoader();
    this.loadComboLookupsByRow();
  }

  loadComboLookupsByRow(): void {
    if (this.j == this.gridRows.length) {
      this.app.hideLoader();
      return;
    }

    let lookupParameters = this.gridRows[this.j].params.filter(parameter => parameter.type == 'combolookup' || parameter.type == 'multiselect');
    let id = this.selectedLines.length > 0 ? this.selectedLines[this.j].id : '';
    for (let i = 0; i < lookupParameters.length; i++) {
      let parameter = lookupParameters[i];      
      parameter.lookupValues = [];
      this.radController.getLookupData(parameter.data, parameter.filterValue, id, this.userId).then(result => {
        parameter.lookupValues = result;
        parameter.filteredLookupValues = [];
        for (let index = 0; index < parameter.lookupValues.length; index++) {
          parameter.filteredLookupValues.push({ lookUpValue: parameter.lookupValues[index], selected: index == 0 ? true : false });
        }
      });
    }
    
    this.j++;
    setTimeout(() => this.loadComboLookupsByRow(), 250);
  }

  addRow(): void {
    this.gridRows.push({
      id: this.gridRows.length + 1,
      params: JSON.parse(JSON.stringify(this.parameters)),
      saveResult: '',
      message: '',
      sqlError: '',
      datesFormatted: false
    });

    this.j = this.gridRows.length - 1;
    this.loadComboLookupsByRow();
  }

  removeRow(gridRow: IGridActionRow): void {
    if (this.gridRows.length == 1) {
      this.app.alertError(this.app.translations.ALERT_Error_RemoveAllRows);
      return;
    }

    this.gridRows.forEach((row, i) => {
      if (row.id == gridRow.id) this.gridRows.splice(i, 1);
    })

    //readjust row ids to be consecutive
    this.gridRows.forEach((row, i) => { row.id = i + 1; })
    if (this.gridRows.length == 0) this.isSavingGrid = false;
  }

  //#region Combos
  updateMultiSelectCombos(event: any, param: IParameter, selectedLookupValue: any): void {
    if (event.checked) {
      param.multiValueArray.push(selectedLookupValue);
      param.value += selectedLookupValue + ',';
    } else {
      param.value = param.value.replace(`${selectedLookupValue},`, '');
      let index = param.multiValueArray.indexOf(selectedLookupValue);
      if (index !== -1) param.multiValueArray.splice(index, 1);
    }
  }

  unselectOption(value: string, param: IParameter): void {
    param.value = param.value.replace(`${value},`, '');
    let index = param.multiValueArray.indexOf(value);
    if (index !== -1) param.multiValueArray.splice(index, 1);
  }

  openDropdown(event: Event, param: IParameter, op: OverlayPanel): void {
    param.opened = true;
    this.openOptions(param);
    op.toggle(event);
  }

  containsString(arr: any[], searchStr: string): boolean {
    return arr.some(item => {
      if (typeof item === 'string') {
        return item.includes(searchStr);
      } else if ('value' in item) {
        return item.value.includes(searchStr);
      }
      return false;
    });
  }

  openOptions(param: IParameter): void {
    param.filteredLookupValues = [];
    for (let index = 0; index < param.lookupValues.length; index++) {
      let element = param.lookupValues[index];
      let selected = false;

      if (!param.value || param.value === '') {
        selected = index === 0;
      } else {
        if (param.type === 'combolookup') {
          selected = param.value === element.value;
        } else {
          selected = param.value === element;
        }
      }

      param.filteredLookupValues.push({ lookUpValue: element, selected });
    }

    param.opened = true;
  }

  change(event: KeyboardEvent, param: IParameter): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter' || event.key === 'Tab') return;
    if (param.value.length > 0) {
      if (param.type == 'combolookup') {
        let a = param.lookupValues.filter(item => item.value.toLowerCase().includes(param.value.toLowerCase()));
        param.filteredLookupValues = [];
        for (let index = 0; index < a.length; index++) {
          param.filteredLookupValues.push({ lookUpValue: a[index], selected: false });
        }
      } else {
        let a = param.lookupValues.filter(item => item.toLowerCase().includes(param.value.toLowerCase()));
        param.filteredLookupValues = [];
        for (let index = 0; index < a.length; index++) {
          param.filteredLookupValues.push({ lookUpValue: a[index], selected: false });
        }
      }

      if (param.filteredLookupValues.length > 0) param.filteredLookupValues[0].selected = true;
    }
    else {
      param.filteredLookupValues = [];
      param.preselectedValue = '';
      for (let index = 0; index < param.lookupValues.length; index++) {
        param.filteredLookupValues.push({ lookUpValue: param.lookupValues[index], selected: false });
      }
    }
  }

  handleBlur(param: IParameter, op: OverlayPanel, row: IGridActionRow): void {
    param.opened = false;
    op.hide();

    if (param.value != '') {
      let res = this.containsString(param.lookupValues, param.value);
      if (!res) {
        param.value = '';
        param.selectedComboId = '-1';
        let childParams = row.params.filter(p => p.filterParameter != null && p.filterParameter == param.caption);
        childParams.forEach(childParam => {
          childParam.value = "";
          childParam.filterValue = "";
          childParam.lookupValues = [];
          childParam.filteredLookupValues = [];
        })
      }
    } else {
      if (param.wasTabSelecting) {
        if (param.filteredLookupValues.length > 0) {
          let lookupValue = param.filteredLookupValues.find(l => l.selected);
          if (lookupValue) {
            param.value = param.type == 'combolookup' ? lookupValue.lookUpValue.value : lookupValue.lookUpValue;
            param.selectedComboId = lookupValue.lookUpValue.id == '-1' ? lookupValue.lookUpValue.value : lookupValue.lookUpValue.id;

            let childParams = row.params.filter(p => p.filterParameter != null && p.filterParameter == param.caption);
            childParams.forEach(childParam => {
              childParam.value = "";
              childParam.filterValue = param.value;
            })

            this.j = 0;
            this.loadLookups(false, childParams, row);
          }
        }
      }
    }

    if (param.wasTabSelecting) {
      param.wasTabSelecting = false;
      return;
    }

    if (param.wasExplicitlySelected) {
      param.wasExplicitlySelected = false;
      return;
    }
  }

  loadLookups(fromTextArea: boolean, params: IParameter[], row: IGridActionRow): void {
    if (this.j == params.length) {
      this.app.hideLoader();
      return;
    }

    let param = params[this.j];
    if (param.type == 'combolookup' || param.type == 'multiselect') {
      param.lookupValues = [];
      let id = this.selectedLines.length > 0 ? this.selectedLines[0].id : '';
      this.radController.getLookupData(param.data, param.filterValue, id, this.userId).then(result => {
        let parentTextField = row.params.find(p => p.isParentLookup && p.type == 'textarea');
        if (fromTextArea && result.length > 0 && result[0].id.includes('ERROR')) {
          if (parentTextField) {
            parentTextField.errorMessage = result[0].value;
            let childParams = row.params.filter(p => p.filterParameter != null && p.filterParameter == parentTextField.caption);
            childParams.forEach(childParam => {
              childParam.value = "";
              childParam.filterValue = "";
              childParam.lookupValues = [];
              childParam.filteredLookupValues = [];
            })
          }
        }
        else {
          param.lookupValues = result;
          param.filteredLookupValues = [];
          for (let index = 0; index < param.lookupValues.length; index++) {
            param.filteredLookupValues.push({ lookUpValue: param.lookupValues[index], selected: index == 0 ? true : false })
          }
        }
      });
    }

    this.j++;
    setTimeout(() => this.loadLookups(fromTextArea, params, row), 250);
  }

  updateCombos(updatedFromTextArea: boolean, parameter: IParameter, selectedLookupValue: any, row: IGridActionRow): void {
    if (!updatedFromTextArea) {
      if (parameter.type == 'combolookup') {
        parameter.value = selectedLookupValue.lookUpValue.value;
        parameter.preselectedValue = selectedLookupValue.lookUpValue.value;
        parameter.selectedComboId = selectedLookupValue.lookUpValue.id == '-1' ? selectedLookupValue.lookUpValue.value : selectedLookupValue.lookUpValue.id;
        let currentSelectedValue = parameter.filteredLookupValues.find(v => v.selected);
        if (currentSelectedValue) {
          currentSelectedValue.selected = false;
        }
        selectedLookupValue.selected = true;
      } else {
        parameter.value = selectedLookupValue.lookUpValue;
        parameter.preselectedValue = selectedLookupValue.lookUpValue;
        let currentSelectedValue = parameter.filteredLookupValues.find(v => v.selected);
        if (currentSelectedValue) {
          currentSelectedValue.selected = false;
        }
        selectedLookupValue.selected = true;
      }
    }

    parameter.opened = false;
    if (!parameter.isParentLookup) return;
    parameter.errorMessage = '';
    let childParams = row.params.filter(param => param.filterParameter != null && param.filterParameter == parameter.caption);
    childParams.forEach(childParam => {
      childParam.filterValue = parameter.value;
      childParam.value = "";
      childParam.lookupValues = [];
      childParam.filteredLookupValues = [];
    });
    this.j = 0;
    this.loadLookups(updatedFromTextArea, childParams, row);
  }

  handleKeyDown(event: KeyboardEvent, row: IGridActionRow): void {
    if (event.key === 'Tab') {
      let param = row.params.find(p => p.opened);
      if (param) {
        if (!param.opened) return;
        param.wasTabSelecting = true;
      }
    }
  }

  handleKeyUp(event: KeyboardEvent, row: IGridActionRow): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter' || event.key === 'Tab') {
      let param = row.params.find(p => p.opened);
      if (param && !param.opened) return;

      if (!param || !param.filteredLookupValues) return;
      let selectedIndex = param.filteredLookupValues.findIndex(v => v.selected);
      if (selectedIndex === -1) return;

      if (event.key === 'ArrowUp' && selectedIndex > 0) {
        param.filteredLookupValues[selectedIndex].selected = false;
        param.filteredLookupValues[selectedIndex - 1].selected = true;
        param.value = '';
        param.selectedComboId = '-1';
        this.scrollToOption(selectedIndex - 1);
      }

      if (event.key === 'ArrowDown' && selectedIndex < param.filteredLookupValues.length - 1) {
        param.filteredLookupValues[selectedIndex].selected = false;
        param.filteredLookupValues[selectedIndex + 1].selected = true;
        param.value = '';
        param.selectedComboId = '-1';
        this.scrollToOption(selectedIndex + 1);
      }

      if (event.key === 'Enter') {
        param.wasExplicitlySelected = true;
        this.updateCombos(false, param, param.filteredLookupValues[selectedIndex], row);
      }

      if (event.key === 'Tab') {
        if (selectedIndex >= 0) {
          param.wasExplicitlySelected = true;
          this.updateCombos(false, param, param.filteredLookupValues[selectedIndex], row);
        }
      }
    }
  }

  @ViewChildren('optionEl', { read: ElementRef }) optionEls!: QueryList<ElementRef>;
  scrollToOption(index: number) {
    let element = this.optionEls.toArray()[index]?.nativeElement;
    if (element) element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
  //#endregion

  updateCheckboxToggles(parameter: IParameter, event: any): void {
    parameter.value = event.checked;
    parameter.preselectedValue = event.checked;
  }

  updateNumericInputs(parameter: IParameter, event: any): void {
    if (parameter.dataType == 'Int32' || parameter.dataType == 'Int16') {
      let value = event.srcElement.value.toString();
      if (value.includes(',') || value.includes('.')) {
        this.app.alertError(`${this.app.translations.ALERT_Error_InvalidDecimal} ${parameter.caption}`);
        this.invalidValues = true;
        return;
      }
    }

    this.invalidValues = false;
    parameter.value = event.srcElement.value.toString();
    parameter.preselectedValue = event.srcElement.value.toString();
  }

  updateTextInputs(parameter: IParameter, event: any): void {
    parameter.value = event.srcElement.value.toString();
    parameter.preselectedValue = event.srcElement.value.toString();
  }

  updateDateInputs(selectedDate: string, parameter: IParameter): void {
    parameter.dateValues.serverDate = selectedDate;
  }

  updateImage(parameter: IParameter, event: any): void {
    if (!event) {
      parameter.value = null;
      parameter.preselectedValue = null;
      return;
    }

    let fileToUpload = event.target.files;
    let file = fileToUpload[0] as File;
    if (!file.name.includes('.png') && !file.name.includes('.jpg')) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidImage);
      return;
    }

    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let base64String = reader.result as string;
      parameter.value = base64String.replace(`data:image/${base64String.charAt(0) == '/' ? 'jpg' : 'png'};base64,`, '');
      parameter.preselectedValue = parameter.value;
    };
  }

  save(): void {
    if (this.gridRows.length == 0) {
      this.app.alertInfo(this.app.translations.ALERT_Info_NothingToSave);
      return;
    }

    if (this.invalidValues) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidValues);
      return;
    }

    for (let index = 0; index < this.gridRows.length; index++) {
      let row = this.gridRows[index];
      for (let k = 0; k < row.params.length; k++) {
        if (row.params[k].highlightRequired) {
          row.params[k].highlightRequired = false;
        }
      }
    }

    let requiredEmptyFields = false;
    for (let index = 0; index < this.gridRows.length; index++) {
      let row = this.gridRows[index];
      let emptyParams = row.params.filter(param => param.value == '' && param.mandatory == 'Y' && param.dataType != 'DateTime');
      let emptyDateParams = row.params.filter(param => (param.dateValues.serverDate == '' || param.dateValues.serverDate == null) && param.mandatory == 'Y' && param.dataType == 'DateTime');
      if (emptyParams.length > 0 || emptyDateParams.length > 0) {
        emptyParams.forEach(param => { param.highlightRequired = true; })
        requiredEmptyFields = true;
      }
    }

    if (!requiredEmptyFields) {
      this.isSavingGrid = true;
      this.allRowsUpdated = true;
      this.j = 0;
      this.saveRows();
    } else {
      this.app.alertError(this.app.translations.ALERT_Error_RequiredFieldEmpty);
    }
  }

  saveRows(): void {
    if (this.j == this.gridRows.length) {
      sessionStorage.removeItem('qx_action_idcheck');    
      if (this.allRowsUpdated) setTimeout(() => this.communicationService.refreshAll(), 1000);
      return;
    }

    let gridRow = this.gridRows[this.j];
    gridRow.sqlError = '';
    if (gridRow.saveResult != 'success') {
      gridRow.saveResult = 'saving';
      let id = '';
      if (this.selectedLines.length == 1) id = this.selectedLines[0].id;
      else if (this.selectedLines.length > 0) id = this.selectedLines[this.j].id;

      for (let i = 0; i < gridRow.params.length; i++) {
        if (gridRow.params[i] && gridRow.params[i].value != '' && gridRow.params[i].type == 'combolookup' && gridRow.params[i].selectedComboId == '-1') {
          let lookupValue = gridRow.params[i].lookupValues.find(lv => lv.value == gridRow.params[i].value);
          if (lookupValue) {
            gridRow.params[i].selectedComboId = lookupValue.id;
          }
        }
      }

      this.radController.createUpdateSingleRecord(this.nodeId, this.userId, this.action.id, id, gridRow.params).then(result => {
        if (result.includes('ERROR') || result.includes('ERORR')) {
          gridRow.saveResult = 'error';
          gridRow.message = result;
          this.allRowsUpdated = false;
        } else {
          gridRow.saveResult = 'success';
          gridRow.message = result;
          if (result == 'noresult' || result == '')
            gridRow.message = this.app.translations.GRID_lbl_ExecuteSuccess;
        }

        this.j++;
        this.saveRows();
      });
    }
  }

  //properly test this
  async pasteExcelData(): Promise<void> {
    let clipboardText = await navigator.clipboard.readText();
    if (clipboardText && clipboardText.includes('\n') && clipboardText.includes('\t')) {
      let rows = clipboardText.trim().split('\n');
      if (rows.length > 0) {
        let workbook = XLSX.read(clipboardText, { type: 'string' });
        let worksheet = workbook.Sheets[workbook.SheetNames[0]];
        let jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        let formattedData = jsonData.map((row: any[]) => {
          return row.reduce((acc, cell, i) => {
            acc[`Column${i + 1}`] = cell;
            return acc;
          }, {});
        });

        this.pastedDataFromExcel = true;
        formattedData.forEach(line => {
          let parametersFromPaste = JSON.parse(JSON.stringify(this.parameters));
          Object.keys(line).forEach((key, i) => {
            let param = parametersFromPaste[i];
            param.preselectedValue = line[key];
            param.value = line[key];
            if (param.isParentLookup) {
              let childParams = parametersFromPaste.filter(parameter => parameter.filterParameter == param.caption);
              childParams.forEach(childParam => { childParam.filterValue = param.preselectedValue; })
            }
          })

          this.gridRows.push({
            id: this.gridRows.length + 1,
            params: parametersFromPaste,
            saveResult: '',
            message: '',
            sqlError: '',
            datesFormatted: false
          });
        })

        this.j = 0;
        this.loadComboLookupsByRow();
        return;
      }
    } else {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidExcelData);
    }
  }
}
